// Video evidence control for the Reports hub — a sibling of the photo
// uploader, NOT an extension of MultipleMediaUploadComponent (that component
// is shared with the image-only result-sheet flow). One clip per report:
// native <input capture> on mobile (the OS camera records better than any
// in-page UI), an in-app MediaRecorder modal on desktop, plus a plain file
// pick on both.
import './video-evidence.scss';

import type React from 'react';
import { type FC, useEffect, useRef, useState } from 'react';

import { MAX_VIDEO_DURATION_SEC } from '../../../interface/vesr';
import { _isMobile } from '../helper';
import type { IFileUploadStatus, UploadPhase } from '../multiple-upload-document';
import { AlertSVG, CheckSVG, RefreshSVG, TrashSVG } from '../svgs';
import TextPrompt from '../text-prompt';
import { formatDuration, validateVideoFile } from './validate';
import VideoRecorderModal from './VideoRecorderModal';

interface IVideoEvidenceFieldProps {
	video: File | null;
	setVideo: (file: File | null) => void;
	/** Live upload status (keyed off the submit machine's per-file map). */
	status?: IFileUploadStatus;
	onRetry?: () => void;
	disabled?: boolean;
}

const VideoGlyph = ({ size = 18 }: { size?: number }) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.7'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<rect
			x='3'
			y='6'
			width='13'
			height='12'
			rx='2.5'
		/>
		<path d='m16 10.5 5-3v9l-5-3' />
	</svg>
);

const phaseLabel: Record<UploadPhase, string> = {
	pending: 'Ready to upload',
	uploading: 'Uploading…',
	success: 'Uploaded & verified',
	error: 'Upload failed',
};

const formatFileSize = (bytes: number) => {
	if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1048576).toFixed(1)} MB`;
};

export const VideoEvidenceField: FC<IVideoEvidenceFieldProps> = ({ video, setVideo, status, onRetry, disabled }) => {
	const captureInputRef = useRef<HTMLInputElement>(null);
	const browseInputRef = useRef<HTMLInputElement>(null);
	const [fileError, setFileError] = useState('');
	const [durationSec, setDurationSec] = useState<number | null>(null);
	const [recorderOpen, setRecorderOpen] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	const ismobile = _isMobile();
	const canRecordInApp = !ismobile && typeof MediaRecorder !== 'undefined';

	// One object URL per selected file — revoked on replace/remove/unmount.
	useEffect(() => {
		if (!video) {
			setPreviewUrl(null);
			return;
		}
		const url = URL.createObjectURL(video);
		setPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [video]);

	const acceptFile = async (file: File) => {
		setFileError('');
		const result = await validateVideoFile(file);
		if (!result.ok) {
			setFileError(result.error || 'This video can’t be used.');
			return;
		}
		setDurationSec(result.durationSec ?? null);
		setVideo(file);
	};

	const handleOnChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		const file = target.files?.[0];
		if (file) void acceptFile(file);
		if (target) target.value = '';
	};

	const handleRemove = () => {
		if (status?.phase === 'uploading') return;
		setVideo(null);
		setDurationSec(null);
		setFileError('');
	};

	const phase: UploadPhase = status?.phase ?? 'pending';
	const isUploading = phase === 'uploading';
	const isError = phase === 'error';
	const isSuccess = phase === 'success';

	return (
		<div className='video-ev'>
			<input
				type='file'
				accept='video/*'
				capture='environment'
				ref={captureInputRef}
				className='upload-result__hidden-input'
				onChange={handleOnChange}
				aria-label='Record video with camera'
			/>
			<input
				type='file'
				accept='video/*'
				ref={browseInputRef}
				className='upload-result__hidden-input'
				onChange={handleOnChange}
				aria-label='Browse for a video file'
			/>

			{!video ? (
				<div className='video-ev__empty'>
					<button
						type='button'
						className='video-ev__record-btn'
						disabled={disabled}
						onClick={() => {
							if (disabled) return;
							if (canRecordInApp) setRecorderOpen(true);
							else captureInputRef.current?.click();
						}}
					>
						<VideoGlyph />
						<span>Record a video</span>
						<span className='video-ev__record-hint'>up to {formatDuration(MAX_VIDEO_DURATION_SEC)}</span>
					</button>
					<button
						type='button'
						className='video-ev__browse-btn'
						disabled={disabled}
						onClick={() => browseInputRef.current?.click()}
					>
						or pick a video from device
					</button>
				</div>
			) : (
				<div className={`upload-result__preview-card upload-result__preview-card--${phase} video-ev__card`}>
					<div className='upload-result__preview-thumb video-ev__thumb'>
						{/* Thumbnail only — native controls are unusable at 52px and just clutter the card. */}
						{previewUrl ? (
							<video
								src={previewUrl}
								muted
								playsInline
								preload='metadata'
							/>
						) : null}
						<span
							className='video-ev__duration'
							aria-hidden='true'
						>
							<VideoGlyph size={11} />
							{durationSec != null ? formatDuration(durationSec) : 'video'}
						</span>
						{isSuccess && (
							<span
								className='upload-result__preview-verified'
								aria-hidden='true'
							>
								<CheckSVG
									color='#fff'
									width='14'
									height='14'
								/>
							</span>
						)}
					</div>
					<div className='upload-result__preview-info'>
						<p className='upload-result__preview-name'>{video.name}</p>
						<p className='upload-result__preview-size'>{formatFileSize(video.size)}</p>

						<span className={`upload-result__status-chip upload-result__status-chip--${phase}`}>
							{isSuccess && (
								<CheckSVG
									color='var(--success-600)'
									width='12'
									height='12'
								/>
							)}
							{isError && <AlertSVG />}
							{phaseLabel[phase]}
						</span>

						{isUploading && (
							<div className='upload-result__card-progress'>
								<div
									className='upload-result__card-progress-bar'
									style={{ width: `${status?.progress || 0}%` }}
									role='progressbar'
									aria-valuenow={status?.progress || 0}
									aria-valuemin={0}
									aria-valuemax={100}
									aria-label={`Uploading ${video.name}`}
								/>
							</div>
						)}

						{isError && (
							<div
								className='upload-result__card-error'
								aria-live='assertive'
							>
								<span>{status?.error || 'Upload failed.'}</span>
								{typeof onRetry === 'function' && (
									<button
										type='button'
										className='upload-result__retry-btn'
										onClick={onRetry}
										aria-label={`Retry uploading ${video.name}`}
									>
										<RefreshSVG />
										Retry
									</button>
								)}
							</div>
						)}
					</div>
					<div className='upload-result__preview-actions'>
						<button
							onClick={handleRemove}
							className='upload-result__preview-btn upload-result__preview-btn--remove'
							aria-label={`Remove ${video.name}`}
							type='button'
							disabled={isUploading || disabled}
						>
							<TrashSVG />
						</button>
					</div>
				</div>
			)}

			{fileError ? (
				<div className='upload-result__error'>
					<TextPrompt
						prompt={fileError}
						status={false}
					/>
				</div>
			) : null}

			{recorderOpen ? (
				<VideoRecorderModal
					onUse={(file) => {
						setRecorderOpen(false);
						void acceptFile(file);
					}}
					onClose={() => setRecorderOpen(false)}
				/>
			) : null}
		</div>
	);
};
